/**
 * Optional body measurements ("Mål") on a listing.
 *
 * Stored as a nested object on `publicData.maal`, e.g.
 *   { livvidde: 72, hoftevidde: 90, bryst: 88, lengde: 110 }
 * with every value a whole number of centimetres.
 *
 * NOTE this deliberately isn't a configured listing field: Sharetribe's
 * listing fields are flat scalars (enum / text / long / boolean), so a nested
 * object can't be expressed there. The trade-off is that `maal` is display
 * data only — it can't be used as a search filter without restructuring into
 * four flat fields.
 *
 * Form fields are flat (`maalLivvidde`, …) rather than dotted paths, so they
 * can't collide with the configured listing-field keys that the panel's
 * submit handler sweeps up.
 */

// Render/persist order — matches the order shown to sellers and buyers.
export const MEASUREMENT_KEYS = ['livvidde', 'hoftevidde', 'bryst', 'lengde'];

// Waist/bust on a pair of shoes is meaningless, so the block is scoped to
// clothing. Bags and accessories are excluded too: only "Lengde" would apply,
// and a per-category subset isn't worth the branching until it's asked for.
const MEASUREMENTS_CATEGORY_LEVEL_1 = 'klar';

export const formFieldName = key => `maal${key.charAt(0).toUpperCase()}${key.slice(1)}`;

/** Should the Mål block be offered / shown for this listing? */
export const showMeasurements = publicData =>
  publicData?.categoryLevel1 === MEASUREMENTS_CATEGORY_LEVEL_1;

/**
 * A measurement is only valid as a positive whole number of cm. Anything else
 * (blank, 0, negative, fractional, non-numeric) is treated as "not provided"
 * rather than an error — the whole block is optional.
 */
const toStoredValue = raw => {
  if (raw === '' || raw == null) {
    return null;
  }
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/** publicData.maal -> flat form values. */
export const measurementsToFormValues = maal => {
  return MEASUREMENT_KEYS.reduce((acc, key) => {
    const value = maal?.[key];
    return value == null ? acc : { ...acc, [formFieldName(key)]: String(value) };
  }, {});
};

/**
 * Flat form values -> publicData.maal.
 *
 * Returns `null` when nothing usable was entered, so the caller can clear the
 * key rather than persisting an empty object.
 */
export const formValuesToMeasurements = values => {
  const maal = MEASUREMENT_KEYS.reduce((acc, key) => {
    const stored = toStoredValue(values?.[formFieldName(key)]);
    return stored == null ? acc : { ...acc, [key]: stored };
  }, {});
  return Object.keys(maal).length > 0 ? maal : null;
};

/**
 * The measurements actually present, in display order — so the PDP can render
 * only the rows the seller filled in.
 */
export const presentMeasurements = maal =>
  MEASUREMENT_KEYS.filter(key => maal?.[key] != null).map(key => ({
    key,
    value: maal[key],
  }));
